// Mobile Time Registration — start/stop timer flow.
// Design: design/time-registration-form/project/Time Registration - Mobile.html
// (and mobile.jsx in the same handoff bundle).
//
// Flow:
//   - Idle hero with "Start tracking" + Quick resume from recents
//   - Picker bottom-sheet cascades Customer → Project → Phase → Activity,
//     with a Mine / All toggle and a phase-budget hint
//   - Running card live-ticks; Stop posts a single time entry covering the
//     [startedAt, endedAt] window; Switch ends and immediately starts the next
//   - Wake Lock API keeps the screen awake while tracking (default on)
//   - Today's log is rendered from /time/entries for the current week
//
// The running timer + recents + scope + keepAwake live in localStorage so the
// device can be locked / refreshed without losing the running task.

import { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Plus, Square, RefreshCcw, Play, Sun, Check, X, Clock, Combine } from 'lucide-react'
import { api } from '@/lib/api/client'
import type {
  TimeEntry, WeekStatus, WeekStatusValue,
  WorkTreeCustomer, WorkTreePhase,
  TimeEntryMergeResponse,
} from '@/types/api'
import TimeEntryEditor from '@/components/time/TimeEntryEditor'
import {
  type TaskRef, type RunningTask,
  useTimerState, useTimerActions, fmtDur, fmtHours, fmtClock,
} from '@/lib/timer'

const pad = (n: number) => String(n).padStart(2, '0')
const EMPTY_ID_SET: ReadonlySet<number> = new Set()

function getWeekStart(offset: number): Date {
  const now = new Date()
  const day = now.getDay()
  const monday = new Date(now)
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7)
  monday.setHours(0, 0, 0, 0)
  return monday
}
function toIso(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}
function addDays(d: Date, n: number): Date {
  const r = new Date(d); r.setDate(d.getDate() + n); return r
}
function getISOWeek(date: Date): number {
  const d = new Date(date); d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7))
  const week1 = new Date(d.getFullYear(), 0, 4)
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
}
function getTodayIndex(weekStart: Date): number {
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const diff = Math.round((today.getTime() - weekStart.getTime()) / 86400000)
  return diff >= 0 && diff <= 6 ? diff : -1
}

const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']

// ─── Page ─────────────────────────────────────────────────────────────────────

interface MobileTimePageProps {
  weekOffset:  number
  onPrevWeek:  () => void
  onNextWeek:  () => void
  onThisWeek:  () => void
}

export default function MobileTimePage({ weekOffset, onPrevWeek, onNextWeek }: MobileTimePageProps) {
  const queryClient = useQueryClient()
  const weekStart    = useMemo(() => getWeekStart(weekOffset), [weekOffset])
  const weekStartStr = useMemo(() => toIso(weekStart), [weekStart])
  const weekNum      = getISOWeek(weekStart)
  const todayIndex   = getTodayIndex(weekStart)
  const todayDate    = useMemo(() => todayIndex >= 0 ? toIso(addDays(weekStart, todayIndex)) : '', [weekStart, todayIndex])

  const [state, setState]         = useTimerState()
  const { startTask, stopRunning, resumeEntry } = useTimerActions()
  const [tick, setTick]           = useState(Date.now())
  const [sheetOpen, setSheetOpen] = useState(false)
  const [activeDayIdx, setActiveDayIdx] = useState(() => todayIndex >= 0 ? todayIndex : 0)

  // WI-11825 — entry editor + selection state.
  const [editingEntry, setEditingEntry]     = useState<TimeEntry | null>(null)
  const [selectedIds, setSelectedIds]       = useState<Set<number>>(new Set())
  const selectionMode = selectedIds.size > 0
  const exitSelection = useCallback(() => setSelectedIds(new Set()), [])

  // Tick the running timer every second.
  useEffect(() => {
    if (!state.running) return
    const id = setInterval(() => setTick(Date.now()), 1000)
    return () => clearInterval(id)
  }, [state.running])

  // Wake Lock API — re-acquired on visibilitychange. No-op on browsers without support.
  const wakeRef = useRef<WakeLockSentinel | null>(null)
  const [awakeActive, setAwakeActive] = useState(false)
  useEffect(() => {
    const want = !!(state.running && state.keepAwake)
    let cancelled = false

    const acquire = async () => {
      if (!('wakeLock' in navigator)) { setAwakeActive(false); return }
      try {
        const lock = await (navigator as Navigator).wakeLock!.request('screen')
        if (cancelled) { lock.release().catch(() => {}); return }
        wakeRef.current = lock
        setAwakeActive(true)
        lock.addEventListener('release', () => {
          if (wakeRef.current === lock) {
            wakeRef.current = null
            setAwakeActive(false)
          }
        })
      } catch { setAwakeActive(false) }
    }
    const release = async () => {
      if (wakeRef.current) {
        try { await wakeRef.current.release() } catch { /* ignore WakeLock release errors */ }
        wakeRef.current = null
      }
      setAwakeActive(false)
    }

    if (want) acquire(); else release()

    const onVisible = () => {
      if (document.visibilityState === 'visible' && want && !wakeRef.current) acquire()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      document.removeEventListener('visibilitychange', onVisible)
      release()
    }
  }, [state.running, state.keepAwake])

  // ─── Data ──────────────────────────────────────────────────────────────────

  const { data: entries = [] } = useQuery({
    queryKey: ['time', 'entries', weekStartStr],
    queryFn:  () => api.get<TimeEntry[]>(`/time/entries?weekStart=${weekStartStr}`),
  })

  const { data: weekStatus } = useQuery({
    queryKey: ['time', 'week', weekStartStr],
    queryFn:  () => api.get<WeekStatus>(`/time/weeks/${weekStartStr}`),
  })

  // Tree for the picker — re-fetched when the user toggles scope.
  const { data: workTree = [] } = useQuery({
    queryKey: ['time', 'work-tree', state.scope],
    queryFn:  () => api.get<WorkTreeCustomer[]>(`/time/work-tree?scope=${state.scope}`),
    enabled:  sheetOpen,
  })

  const status     = (weekStatus?.status ?? 'draft') as WeekStatusValue
  const isReadOnly = status === 'submitted' || status === 'approved' || !!weekStatus?.invoicedAt

  // Active day = where "Today's log" reads from.
  const activeDate = useMemo(() => toIso(addDays(weekStart, activeDayIdx)), [weekStart, activeDayIdx])
  const dayEntries = useMemo(() => entries.filter(e => e.entryDate === activeDate), [entries, activeDate])

  const totalToday = useMemo(() => {
    let ms = dayEntries.reduce((s, e) => s + e.workedHrs * 3_600_000, 0)
    if (state.running && activeDate === todayDate) ms += tick - state.running.startedAt
    return ms
  }, [dayEntries, state.running, tick, activeDate, todayDate])

  const totalWorked = entries.reduce((s, e) => s + e.workedHrs, 0)

  // ─── Actions ───────────────────────────────────────────────────────────────

  const handlePick = useCallback((task: TaskRef) => {
    startTask(task)
    setTick(Date.now())
    setSheetOpen(false)
  }, [startTask])

  const handleResume = useCallback((entry: TimeEntry) => {
    resumeEntry(entry)
    setTick(Date.now())
  }, [resumeEntry])

  const setScope = useCallback((scope: 'mine' | 'all') => {
    setState(prev => ({ ...prev, scope }))
  }, [setState])

  const setKeepAwake = useCallback((v: boolean) => {
    setState(prev => ({ ...prev, keepAwake: v }))
  }, [setState])

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="mt-shell">

      {/* Week bar */}
      <div className="mt-week-bar">
        <button className="mt-wk-arrow" onClick={onPrevWeek} aria-label="Previous week">
          <ChevronLeft size={18} />
        </button>
        <div className="mt-wk-info">
          <span className="mt-wk-num">Week {weekNum}</span>
          <span className="mt-wk-stats">
            {fmtHours(totalToday)} today · {totalWorked.toFixed(1)}h this week
          </span>
        </div>
        {statusBadge(status, !!weekStatus?.invoicedAt)}
        <button className="mt-wk-arrow" onClick={onNextWeek} aria-label="Next week">
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Day strip */}
      <div className="day-picker" style={{ flexShrink: 0 }}>
        {DAY_LETTERS.map((letter, i) => {
          const date    = addDays(weekStart, i)
          const isActive = i === activeDayIdx
          const isToday  = i === todayIndex
          const dayHours = entries
            .filter(e => e.entryDate === toIso(date))
            .reduce((s, e) => s + e.workedHrs, 0)
          const barColor = dayHours >= 8 ? 'var(--ns-brand-strong)'
                         : dayHours > 0 ? 'var(--ns-border-strong)'
                         : 'transparent'
          return (
            <button
              key={i}
              className={`day-picker-col${isActive ? ' active' : ''}${isToday ? ' today' : ''}`}
              onClick={() => setActiveDayIdx(i)}
            >
              <span className="day-letter">{letter}</span>
              <span className="day-num">{date.getDate()}</span>
              <div className="day-underline" style={{ background: barColor }} />
            </button>
          )
        })}
      </div>

      {/* Body */}
      <div className="mt-content">
        {state.running ? (
          <RunningCard
            running={state.running}
            tick={tick}
            onStop={stopRunning}
            onSwitch={() => setSheetOpen(true)}
            keepAwake={state.keepAwake}
            setKeepAwake={setKeepAwake}
            awakeActive={awakeActive}
          />
        ) : (
          <IdleHero
            onStart={() => setSheetOpen(true)}
            recents={state.recents}
            onResume={handlePick}
            disabled={isReadOnly}
          />
        )}

        <div className="mt-entries-head">
          <span className="mt-entries-title">
            {activeDate === todayDate ? "Today's log" : `${DAY_LETTERS[activeDayIdx]} · ${activeDate}`}
          </span>
          <span className="mt-entries-total">{fmtHours(totalToday)}</span>
        </div>
        {selectionMode && (
          <SelectionBar
            entries={dayEntries.filter(e => selectedIds.has(e.id))}
            onCancel={exitSelection}
            onMerged={exitSelection}
          />
        )}
        <EntryList
          entries={dayEntries}
          selectionMode={selectionMode}
          selectedIds={selectedIds}
          // Once any entry is picked, lock the merge group to its activity/date.
          // Other rows render dimmed and can't be ticked.
          lockKey={(() => {
            for (const e of dayEntries) if (selectedIds.has(e.id)) return entryGroupKey(e)
            return null
          })()}
          locked={isReadOnly}
          resumableIds={state.running ? EMPTY_ID_SET : new Set(state.timerEntryIds)}
          onResume={handleResume}
          onTap={e => {
            if (selectionMode) {
              setSelectedIds(prev => {
                const next = new Set(prev)
                next.has(e.id) ? next.delete(e.id) : next.add(e.id)
                return next
              })
            } else if (!isReadOnly) {
              setEditingEntry(e)
            }
          }}
          onLongPress={e => {
            if (isReadOnly) return
            setSelectedIds(prev => {
              const next = new Set(prev)
              next.add(e.id)
              return next
            })
          }}
        />

        <div className="mt-bottom-pad" />
      </div>

      {/* FAB — quick open the picker */}
      {!isReadOnly && !selectionMode && (
        <button className="fab" onClick={() => setSheetOpen(true)} aria-label="Start a task">
          <Plus size={24} />
        </button>
      )}

      {sheetOpen && (
        <PickerSheet
          open={sheetOpen}
          tree={workTree}
          scope={state.scope}
          setScope={setScope}
          currentTask={state.running?.task}
          onClose={() => setSheetOpen(false)}
          onPick={handlePick}
        />
      )}

      {editingEntry && (
        <TimeEntryEditor
          entry={editingEntry}
          weekStatus={status}
          mode="sheet"
          onClose={() => setEditingEntry(null)}
        />
      )}
    </div>
  )
}

// ── Selection toolbar (above the entry list) ────────────────────────────────

function SelectionBar({ entries, onCancel, onMerged }: {
  entries: TimeEntry[]
  onCancel: () => void
  onMerged: () => void
}) {
  const queryClient = useQueryClient()
  const canMerge    = entries.length >= 2

  const merge = useMutation({
    mutationFn: (ids: number[]) =>
      api.post<TimeEntryMergeResponse>('/time/entries-merge', { entryIds: ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['time', 'entries'] })
      onMerged()
    },
  })

  return (
    <>
      <div className="te-select-bar">
        <span className="te-select-count">{entries.length} selected</span>
        {entries.length === 1 && (
          <span className="muted" style={{ fontSize: 11.5 }}>
            Pick one more on the same task
          </span>
        )}
        <span className="te-select-spacer" />
        <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
        <button
          className="btn btn-brand"
          disabled={!canMerge || merge.isPending}
          onClick={() => merge.mutate(entries.map(e => e.id))}
          title={canMerge ? 'Merge selected entries' : 'Pick 2+ entries on the same task'}
        >
          <Combine size={14} />
          {merge.isPending ? 'Merging…' : `Merge (${entries.length})`}
        </button>
      </div>
      {merge.isError && (
        <div className="te-error" style={{ marginBottom: 12 }}>
          Merge failed: {(merge.error as Error).message}
        </div>
      )}
    </>
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function statusBadge(status: WeekStatusValue, invoiced: boolean) {
  if (invoiced)              return <span className="badge badge-ok">Invoiced</span>
  if (status === 'submitted') return <span className="badge badge-warn">Submitted</span>
  if (status === 'approved')  return <span className="badge badge-ok">Approved</span>
  if (status === 'rejected')  return <span className="badge badge-err">Rejected</span>
  return <span className="badge badge-info">Draft</span>
}

// ─── Running card ─────────────────────────────────────────────────────────────

function RunningCard({
  running, tick, onStop, onSwitch, keepAwake, setKeepAwake, awakeActive,
}: {
  running:      RunningTask
  tick:         number
  onStop:       () => void
  onSwitch:     () => void
  keepAwake:    boolean
  setKeepAwake: (v: boolean) => void
  awakeActive:  boolean
}) {
  const elapsed = tick - running.startedAt
  const t       = running.task
  return (
    <div className="mt-run-card">
      <div className="mt-run-head">
        <span className="mt-pulse" />
        <span className="mt-run-tag">Tracking now</span>
        <span className="mt-run-since">since {fmtClock(running.startedAt)}</span>
      </div>
      <div className="mt-run-task">
        <div className="mt-run-customer">{t.customerName}</div>
        <div className="mt-run-activity">{t.projectName} · {t.phaseName} · {t.activityName}</div>
      </div>
      <div className="mt-timer">{fmtDur(elapsed)}</div>
      <div className="mt-run-actions">
        <button className="mt-btn-stop" onClick={onStop}>
          <Square size={14} fill="currentColor" />
          Stop
        </button>
        <button className="mt-btn-switch" onClick={onSwitch}>
          <RefreshCcw size={14} />
          Switch task
        </button>
      </div>
      <label className="mt-awake-row">
        <input
          type="checkbox"
          checked={keepAwake}
          onChange={e => setKeepAwake(e.target.checked)}
        />
        <span className="mt-awake-check" aria-hidden="true">
          <Check size={11} strokeWidth={3} color="white" />
        </span>
        <Sun size={14} style={{ flexShrink: 0, opacity: 0.65 }} />
        <span className="mt-awake-label">Keep screen awake while tracking</span>
        {keepAwake && awakeActive && <span className="mt-awake-pill">on</span>}
      </label>
    </div>
  )
}

// ─── Idle hero ────────────────────────────────────────────────────────────────

function IdleHero({
  onStart, recents, onResume, disabled,
}: {
  onStart:  () => void
  recents:  TaskRef[]
  onResume: (task: TaskRef) => void
  disabled: boolean
}) {
  return (
    <div className="mt-idle-hero">
      <button className="mt-start-btn" onClick={onStart} disabled={disabled}>
        <Play size={18} fill="currentColor" />
        {disabled ? 'Week is locked' : 'Start tracking'}
      </button>
      <div className="mt-not-tracking">Not tracking right now</div>
      {recents.length > 0 && !disabled && (
        <div className="mt-quick-resume">
          <div className="mt-quick-label">Quick resume</div>
          <div className="mt-recent-list">
            {recents.slice(0, 3).map((task, i) => (
              <button key={i} className="mt-recent-row" onClick={() => onResume(task)}>
                <div className="mt-recent-meta">
                  <div className="mt-recent-customer">{task.customerName}</div>
                  <div className="mt-recent-activity">
                    {task.projectName} · {task.activityName}
                  </div>
                </div>
                <Play size={14} fill="currentColor" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Today's log ──────────────────────────────────────────────────────────────

function entryGroupKey(e: TimeEntry): string {
  return `${e.projectId ?? 'n'}::${e.phaseId ?? 'n'}::${e.activityId ?? 'n'}::${e.entryDate}`
}

function EntryList({ entries, selectionMode, selectedIds, lockKey, locked, resumableIds, onResume, onTap, onLongPress }: {
  entries:        TimeEntry[]
  selectionMode:  boolean
  selectedIds:    Set<number>
  lockKey:        string | null
  locked:         boolean
  resumableIds:   ReadonlySet<number>
  onResume:       (e: TimeEntry) => void
  onTap:          (e: TimeEntry) => void
  onLongPress:    (e: TimeEntry) => void
}) {
  if (!entries.length) {
    return (
      <div className="mt-empty">
        <Clock size={22} style={{ opacity: 0.5 }} />
        <div className="mt-empty-1">No entries for this day</div>
        <div className="mt-empty-2">Tap Start tracking or + to add</div>
      </div>
    )
  }
  // Latest first
  const sorted = [...entries].sort((a, b) => (b.id - a.id))
  return (
    <div className="mt-entries">
      {sorted.map(e => {
        const dim = lockKey !== null && entryGroupKey(e) !== lockKey
        return (
          <EntryRow
            key={e.id}
            entry={e}
            selected={selectedIds.has(e.id)}
            selectionMode={selectionMode}
            dim={dim}
            locked={locked}
            resumable={!selectionMode && !locked && resumableIds.has(e.id)}
            onResume={() => onResume(e)}
            onTap={() => onTap(e)}
            onLongPress={() => onLongPress(e)}
          />
        )
      })}
    </div>
  )
}

function EntryRow({ entry, selected, selectionMode, dim, locked, resumable, onResume, onTap, onLongPress }: {
  entry: TimeEntry
  selected: boolean
  selectionMode: boolean
  dim: boolean
  locked: boolean
  resumable: boolean
  onResume: () => void
  onTap: () => void
  onLongPress: () => void
}) {
  const longPressRef = useRef<number | null>(null)
  const movedRef     = useRef(false)
  const inert        = locked || (dim && selectionMode)

  const startPress = () => {
    movedRef.current = false
    if (inert) return
    longPressRef.current = window.setTimeout(() => {
      longPressRef.current = null
      if (!movedRef.current) onLongPress()
    }, 500)
  }
  const cancelPress = () => {
    if (longPressRef.current != null) {
      clearTimeout(longPressRef.current)
      longPressRef.current = null
    }
  }
  const endPress = () => {
    if (longPressRef.current != null) {
      clearTimeout(longPressRef.current)
      longPressRef.current = null
      if (!movedRef.current && !inert) onTap()
    }
  }

  return (
    <div
      className={`mt-entry${selected ? ' mt-entry-selected' : ''}${dim ? ' mt-entry-dim' : ''}`}
      style={{
        cursor:  inert ? 'default' : 'pointer',
        opacity: locked ? 0.7 : (dim ? 0.45 : 1),
      }}
      title={dim ? 'Different task or day — clear the selection to pick this one' : undefined}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerLeave={cancelPress}
      onPointerMove={() => { movedRef.current = true }}
      onContextMenu={e => { e.preventDefault(); if (!inert) onLongPress() }}
    >
      <div className="mt-entry-bar" />
      <div className="mt-entry-body">
        <div className="mt-entry-line">
          <div className="mt-entry-customer">{entry.customerName ?? 'Internal'}</div>
          <div className="mt-entry-dur">{fmtHours(entry.workedHrs * 3_600_000)}</div>
        </div>
        <div className="mt-entry-line2">
          <div className="mt-entry-activity">
            {entry.projectName ?? 'Internal'}
            {entry.activityName ? ` · ${entry.activityName}` : ''}
          </div>
          <div className="mt-entry-time">
            {resumable && (
              <button
                type="button"
                className="mt-entry-resume"
                onPointerDown={ev => ev.stopPropagation()}
                onPointerUp={ev => ev.stopPropagation()}
                onClick={ev => { ev.stopPropagation(); onResume() }}
                title="Resume timer on this entry"
              >
                <Play size={11} fill="currentColor" />
                Resume
              </button>
            )}
            {selectionMode && (
              <span className={`mt-entry-check${selected ? ' on' : ''}`}>
                {selected ? <Check size={12} /> : null}
              </span>
            )}
          </div>
        </div>
        {(entry.notes || entry.invoiceText) && (
          <div className="mt-entry-note">
            {entry.invoiceText && <span className="mt-entry-note-tag">📄</span>}
            {entry.invoiceText ?? entry.notes}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Picker sheet ─────────────────────────────────────────────────────────────

export function PickerSheet({
  open, tree, scope, setScope, currentTask, onClose, onPick,
}: {
  open:        boolean
  tree:        WorkTreeCustomer[]
  scope:       'mine' | 'all'
  setScope:    (s: 'mine' | 'all') => void
  currentTask: TaskRef | undefined
  onClose:     () => void
  onPick:      (task: TaskRef) => void
}) {
  const [customerId, setCustomerId] = useState<string | number | null>(null)
  const [projectId,  setProjectId]  = useState<number | null>(null)
  const [phaseId,    setPhaseId]    = useState<number | null>(null)
  const [activityId, setActivityId] = useState<number | null>(null)

  // Reset when scope flips or sheet closes.
  useEffect(() => {
    if (!open) return
    setCustomerId(null); setProjectId(null); setPhaseId(null); setActivityId(null)
  }, [open, scope])

  const customer = tree.find(c => c.id === customerId) ?? null
  const project  = customer?.projects.find(p => p.id === projectId) ?? null
  const phase: WorkTreePhase | null = project?.phases.find(p => p.id === phaseId) ?? null

  // Auto-cascade: if there's only one option at the next level, pick it.
  useEffect(() => {
    if (!customer) return
    if (customer.projects.length === 1) {
      const p = customer.projects[0]
      setProjectId(p.id)
      if (p.phases.length === 1) {
        const ph = p.phases[0]
        setPhaseId(ph.id)
        if (ph.activities.length === 1) setActivityId(ph.activities[0].id)
      }
    }
  }, [customerId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (project && project.phases.length === 1) setPhaseId(project.phases[0].id)
  }, [projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase && phase.activities.length === 1) setActivityId(phase.activities[0].id)
  }, [phaseId]) // eslint-disable-line react-hooks/exhaustive-deps

  const canStart = customerId != null && projectId != null && phaseId != null && activityId != null

  const handleStart = () => {
    if (!canStart || !customer || !project || !phase) return
    const activity = phase.activities.find(a => a.id === activityId)
    if (!activity) return
    onPick({
      customerId:    customer.id,
      customerName:  customer.name,
      projectId:     project.id,
      projectName:   project.name,
      phaseId:       phase.id,
      phaseName:     phase.name,
      activityId:    activity.id,
      activityName:  activity.name,
    })
  }

  // Budget hint (only when phase has assignment)
  let hint: React.ReactNode = null
  if (phase?.assignment) {
    const { hours, hoursLogged } = phase.assignment
    const remaining = hours - hoursLogged
    const tone = remaining < 0 ? 'danger'
               : remaining < hours * 0.15 ? 'warn'
               : 'good'
    hint = (
      <div className={`mt-budget-hint tone-${tone}`}>
        <span>{remaining < 0 ? 'Over budget' : 'Remaining'}</span>
        <strong>{remaining < 0 ? `+${(-remaining).toFixed(1)}h` : `${remaining.toFixed(1)}h`}</strong>
        <span className="mt-budget-of">of {hours}h</span>
      </div>
    )
  }

  return (
    <>
      <div className="bottom-sheet-backdrop" onClick={onClose} />
      <div className="bottom-sheet">
        <div className="bottom-sheet-handle" />
        <div className="bottom-sheet-head">
          <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {currentTask ? 'Switch to…' : 'What are you working on?'}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ width: 32, height: 32, fontSize: 22, color: 'var(--ns-text-muted)', borderRadius: '50%', border: 'none', background: 'none', cursor: 'pointer', display: 'grid', placeItems: 'center' }}
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-sheet-scope">
          <div className="mt-seg">
            <button className={scope === 'mine' ? 'on' : ''} onClick={() => setScope('mine')}>Mine</button>
            <button className={scope === 'all'  ? 'on' : ''} onClick={() => setScope('all')}>All</button>
          </div>
          <span className="mt-seg-hint">
            {scope === 'mine' ? 'Only your assignments' : 'All open phases'}
          </span>
        </div>

        <div className="bottom-sheet-body" style={{ padding: '4px 18px 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SelectField
            label="Customer"
            value={customerId == null ? '' : String(customerId)}
            onChange={v => {
              setCustomerId(v === '' ? null : (isNaN(Number(v)) ? v : Number(v)))
              setProjectId(null); setPhaseId(null); setActivityId(null)
            }}
            options={tree.map(c => ({ id: String(c.id), name: c.name }))}
            placeholder="Select customer"
          />
          <SelectField
            label="Project"
            value={projectId ? String(projectId) : ''}
            onChange={v => { setProjectId(v ? Number(v) : null); setPhaseId(null); setActivityId(null) }}
            options={(customer?.projects ?? []).map(p => ({ id: String(p.id), name: p.name }))}
            placeholder="Select project"
            disabled={!customer}
          />
          <SelectField
            label="Phase"
            value={phaseId ? String(phaseId) : ''}
            onChange={v => { setPhaseId(v ? Number(v) : null); setActivityId(null) }}
            options={(project?.phases ?? []).map(p => ({ id: String(p.id), name: p.name }))}
            placeholder="Select phase"
            disabled={!project}
          />
          <SelectField
            label="Activity"
            value={activityId ? String(activityId) : ''}
            onChange={v => setActivityId(v ? Number(v) : null)}
            options={(phase?.activities ?? []).map(a => ({ id: String(a.id), name: a.name }))}
            placeholder="Select activity"
            disabled={!phase}
          />
          {hint}
        </div>

        <div className="bottom-sheet-foot">
          <button className="mt-cta" disabled={!canStart} onClick={handleStart}>
            <Play size={16} fill="currentColor" />
            {currentTask ? 'Switch & start' : 'Start tracking'}
          </button>
        </div>
      </div>
    </>
  )
}

function SelectField({
  label, value, onChange, options, placeholder, disabled,
}: {
  label:       string
  value:       string
  onChange:    (v: string) => void
  options:     { id: string; name: string }[]
  placeholder: string
  disabled?:   boolean
}) {
  return (
    <div className="mt-sf">
      <label>{label}</label>
      <div className={`mt-sf-wrap${disabled ? ' dis' : ''}`}>
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}>
          <option value="">{placeholder}</option>
          {options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <ChevronRight size={14} className="mt-caret" style={{ transform: 'rotate(90deg)' }} />
      </div>
    </div>
  )
}
